import { Box, BoxProps, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { produce } from 'immer';
import React, { ReactElement, ReactNode, useEffect, useReducer } from 'react';
import { Link } from 'components/lib';

export interface AccordianProps extends BoxProps {
  title: string;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  children?: ReactNode;
}

interface StateType {
  isControlled: boolean;
  isOpen: boolean;
}

type ActionType =
  | {
      type: 'open';
    }
  | {
      type: 'close';
    };

const reducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case 'open':
      return produce(state, draft => {
        draft.isOpen = true;
      });
    case 'close':
      return produce(state, draft => {
        draft.isOpen = false;
      });
    default:
      throw new Error();
  }
};

export const Accordian = ({
  title,
  open,
  onOpen,
  onClose,
  children,
  ...boxProps
}: AccordianProps): ReactElement => {
  const [state, dispatch] = useReducer(reducer, {
    isControlled: open !== undefined,
    isOpen: !!open,
  });

  const handleToggle = () => {
    if (isControlled) return isOpen ? onClose?.() : onOpen?.();
    dispatch({ type: isOpen ? 'close' : 'open' });
  };

  useEffect(() => {
    if (open !== undefined) dispatch({ type: open ? 'open' : 'close' });
  }, [open]);

  const { isControlled, isOpen } = state;

  return (
    <Box
      sx={{
        width: 730,
        maxWidth: '100%',
        borderRadius: 20,
        paddingBottom: 20,
        cursor: isOpen ? 'default' : 'pointer',
        background: isOpen ? '#fff' : 'none',
        boxShadow: isOpen ? '0 0 10px #e8e8e8' : 'none',
        '&:hover': {
          background: '#fff',
          boxShadow: '0 0 10px #e8e8e8',
        },
        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
      }}
      {...boxProps}
      onClick={() => !isOpen && handleToggle()}
    >
      <Box
        p={20}
        pb={0}
        onClick={() => isOpen && handleToggle()}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{
          cursor: 'pointer',
          WebkitTapHighlightColor: 'rgba(0,0,0,0)',
          userSelect: 'none',
        }}
      >
        <Link onClick={handleToggle}>
          <Typography
            variant="h3"
            color="primary"
            style={{
              fontSize: '1em',
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
        </Link>
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </Box>
      <Box
        sx={{
          overflow: 'hidden',
          paddingLeft: 20,
          paddingRight: 20,
          transition: 'transform 0.25s, max-height 0.2s',
          transform: isOpen ? 'none' : 'scaleY(0)',
          transformOrigin: 'top',
          maxHeight: isOpen ? 1000 : 0,
        }}
        fontSize={{ xs: '0.9em', md: '1em' }}
      >
        {children}
      </Box>
    </Box>
  );
};
